import axios from "axios";
import { ORG_APIS } from "./api_config";

/**
 * Token lifecycle telemetry — when a user logs in and when a token is issued.
 *
 * NOTE: the refresh token is an HttpOnly cookie, so JavaScript can never see
 * it. We can only record that a refresh happened and whether it worked. If you
 * need the refresh token's own created_at / expiry, the backend has to return
 * them in the login and refresh response bodies.
 */
export type TokenEvent =
  | "USER_LOGIN" // user completed authentication
  | "ACCESS_TOKEN_ISSUED" // an access token was written to storage
  | "REFRESH_FAILED"; // refresh call did not return a usable token

// Set to true ONLY for a short, deliberate debugging window. A raw access
// token in the click-log stream is a live bearer credential for the whole of
// its ~30 minute life — anyone who can read logs can act as that doctor.
// The fingerprint below is enough to tell one token from another, which is
// what the Blue7 investigation actually needs.
const LOG_RAW_ACCESS_TOKEN = false;

/**
 * Identifies the current access token without exposing it.
 * `token_fingerprint` is the last 12 chars of the JWT signature — unique
 * enough to correlate rows and prove whether a token changed, useless as a
 * credential on its own.
 */
const describeTokens = () => {
  const bearer = localStorage.getItem("access_token");
  const jwt = bearer ? bearer.replace(/^bearer\s+/i, "").trim() : "";

  return {
    access_token_present: !!jwt,
    access_token_fingerprint: jwt ? jwt.slice(-12) : undefined,
    access_token_length: jwt ? jwt.length : 0,
    ...(LOG_RAW_ACCESS_TOKEN && jwt ? { access_token_raw: bearer } : {}),

    // Names only, never values. The refresh cookie is HttpOnly so it should
    // NOT appear here — if it ever does, it is not HttpOnly and that is a bug
    // worth knowing about. Presence of the refresh token itself cannot be
    // detected from JS; it is only observable through refresh outcomes.
    js_visible_cookie_names: document.cookie
      ? document.cookie.split(";").map((c) => c.split("=")[0].trim())
      : [],
  };
};

/**
 * Who is logged in, held in memory rather than localStorage.
 *
 * This exists because the failure we are chasing may BE localStorage being
 * wiped. Diagnostics that read clinic/user from localStorage arrive anonymous
 * at exactly the moment they matter — unfilterable, unattributable to a
 * clinic. An in-memory copy survives any storage clear for the life of the
 * page, so the one report that counts still says who it was.
 */
let identity: { vms_user_id?: string; clinic_id?: string; clinic_name?: string } = {};

export const setDiagnosticIdentity = (next: typeof identity) => {
  identity = { ...identity, ...next };
};

export const getDiagnosticIdentity = () => ({
  // Prefer the in-memory copy; fall back to storage if it was never set.
  vms_user_id: identity.vms_user_id ?? localStorage.getItem("user_email") ?? undefined,
  clinic_id: identity.clinic_id ?? localStorage.getItem("clinic") ?? undefined,
  clinic_name: identity.clinic_name ?? localStorage.getItem("clinic_name") ?? undefined,
});

export const logTokenEvent = (action: TokenEvent, detail?: Record<string, unknown>) => {
  try {
    const now = Date.now();
    const body = {
      event_type: "VMS_TOKEN_DIAGNOSTIC",
      event_action: action,
      scr_id: window?.location?.pathname,
      source: window?.location?.href,
      ...getDiagnosticIdentity(),
      filter_values: {
        created_at: new Date(now).toISOString(),
        ...describeTokens(),
        ...detail,
      },
      timestamp: now,
    };

    axios
      .post(ORG_APIS?.CLICK_LOG_API?.baseURL ?? "", body, {
        headers: { "Content-Type": "application/json" },
      })
      .catch(() => {
        /* swallow — telemetry is best-effort and must never break auth */
      });
  } catch {
    /* never throw from diagnostics */
  }
};
